import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDocumentSearch, useRecentDocuments } from '../../hooks/useCrmData';
import {
  COLORS, SHADOWS, Icons, TH, TD,
  ErrorBlock,
  PageHeader, EmptyState, SkeletonTable, IconTile,
} from '../ui/crm-ui';

interface Props {
  baseApiUrl: string;
}

function fileExt(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
}

function formatDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

type SortKey = 'modified_desc' | 'modified_asc' | 'name_asc' | 'name_desc';
type ViewMode = 'list' | 'grid';

const SORT_OPTIONS: Array<{ label: string; value: SortKey }> = [
  { label: 'Newest first', value: 'modified_desc' },
  { label: 'Oldest first', value: 'modified_asc' },
  { label: 'Name A-Z',     value: 'name_asc' },
  { label: 'Name Z-A',     value: 'name_desc' },
];

const DocumentsView: React.FC<Props> = ({ baseApiUrl }) => {
  const location = useLocation();
  const [input, setInput] = useState('');
  const [debounced, setDebounced] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('modified_desc');
  const [fileTypeFilter, setFileTypeFilter] = useState('');

  // Pre-apply folder filter from ?folder= URL param (e.g. navigating from a notification)
  const initialFolder = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('folder') ?? '';
  }, [location.search]);
  const [folderFilter, setFolderFilter] = useState(initialFolder);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  const filterPanelRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // 300ms debounce
  useEffect(() => {
    const t = setTimeout(() => setDebounced(input.trim()), 300);
    return () => clearTimeout(t);
  }, [input]);

  // Cmd/Ctrl-K focuses the search input
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  // Close filter panel on outside click
  useEffect(() => {
    if (!showFilterPanel) return;
    function handleMouseDown(e: MouseEvent) {
      if (filterPanelRef.current && !filterPanelRef.current.contains(e.target as Node)) {
        setShowFilterPanel(false);
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [showFilterPanel]);

  const isSearching = debounced.length > 0;
  const recent = useRecentDocuments(baseApiUrl, 50);
  const search = useDocumentSearch(baseApiUrl, debounced);
  const active = isSearching ? search : recent;
  const { data, loading, error, refetch } = active;

  // Derive available file types from current data
  const availableTypes = useMemo(() => {
    const exts = new Set<string>();
    (data || []).forEach((d) => { const ext = fileExt(d.name); if (ext) exts.add(ext); });
    return ['', ...Array.from(exts).sort()];
  }, [data]);

  // Derive available folders from current data
  const availableFolders = useMemo(() => {
    const folders = new Set<string>();
    (data || []).forEach((d) => {
      const folder = d.parentPath.split('/').filter(Boolean).slice(-1)[0] || '';
      if (folder) folders.add(folder);
    });
    return ['', ...Array.from(folders).sort()];
  }, [data]);

  // Apply filters then sort
  const filteredAndSorted = useMemo(() => {
    let items = data || [];
    if (fileTypeFilter) items = items.filter((d) => fileExt(d.name) === fileTypeFilter);
    if (folderFilter) {
      items = items.filter((d) => {
        const folder = d.parentPath.split('/').filter(Boolean).slice(-1)[0] || '';
        return folder === folderFilter;
      });
    }
    items = [...items].sort((a, b) => {
      if (sortKey === 'modified_desc' || sortKey === 'modified_asc') {
        const aT = a.lastModified ? new Date(a.lastModified).getTime() : 0;
        const bT = b.lastModified ? new Date(b.lastModified).getTime() : 0;
        return sortKey === 'modified_desc' ? bT - aT : aT - bT;
      }
      const an = a.name.toLowerCase();
      const bn = b.name.toLowerCase();
      return sortKey === 'name_asc' ? an.localeCompare(bn) : bn.localeCompare(an);
    });
    return items;
  }, [data, sortKey, fileTypeFilter, folderFilter]);

  const hasActiveFilter = fileTypeFilter !== '' || folderFilter !== '' || sortKey !== 'modified_desc';
  const sortLabel = SORT_OPTIONS.find((o) => o.value === sortKey)?.label ?? '';

  const optionBtnStyle = (selected: boolean): React.CSSProperties => ({
    padding: '6px 12px', borderRadius: 999, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
    fontWeight: 600,
    background: selected ? COLORS.blue : COLORS.white,
    color: selected ? '#fff' : COLORS.mid,
    border: `1px solid ${selected ? COLORS.blue : COLORS.border}`,
    transition: 'all 0.12s',
  });

  const headerActions = (
    <>
      {/* View mode toggle */}
      <div style={{
        display: 'inline-flex', padding: 3, gap: 2,
        background: COLORS.light, borderRadius: 8,
      }}>
        {(['list', 'grid'] as const).map((v) => {
          const active = viewMode === v;
          return (
            <button
              key={v}
              type="button"
              onClick={() => setViewMode(v)}
              title={v === 'list' ? 'List view' : 'Grid view'}
              style={{
                padding: '6px 10px', border: 'none', borderRadius: 6, cursor: 'pointer',
                background: active ? COLORS.white : 'transparent',
                color: active ? COLORS.blue : COLORS.mid,
                boxShadow: active ? SHADOWS.sm : 'none',
                fontFamily: 'inherit', fontSize: 12, fontWeight: 600,
                display: 'inline-flex', alignItems: 'center', gap: 5,
                transition: 'all 0.12s',
              }}
            >
              {v === 'list' ? Icons.list() : Icons.grid()} {v === 'list' ? 'List' : 'Grid'}
            </button>
          );
        })}
      </div>

      {/* Refresh */}
      <button
        onClick={refetch}
        disabled={loading}
        title="Refresh document list"
        style={{
          padding: '8px 14px', borderRadius: 8,
          border: `1px solid ${COLORS.border}`,
          background: COLORS.white,
          cursor: loading ? 'not-allowed' : 'pointer',
          opacity: loading ? 0.6 : 1,
          fontSize: 13, fontFamily: 'inherit',
          display: 'inline-flex', alignItems: 'center', gap: 6,
        }}
      >
        <span className={loading ? 'crm-anim-pulseSoft' : ''} style={{ display: 'inline-flex' }}>
          {Icons.refresh(COLORS.mid)}
        </span>
        {loading ? 'Refreshing…' : 'Refresh'}
      </button>
    </>
  );

  return (
    <div className="crm-anim-fadeUp">
      <PageHeader
        icon={Icons.docs(COLORS.blue)}
        title="Documents"
        subtitle="Search, filter and browse every document indexed across SharePoint."
        actions={headerActions}
        accent="brand"
      />

      {/* Search + Filter row */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Search input — large, command-palette feel */}
        <div className="crm-focus-ring" style={{
          flex: 1, minWidth: 280, position: 'relative',
          background: COLORS.white,
          border: `1px solid ${COLORS.border}`, borderRadius: 12,
          boxShadow: SHADOWS.sm,
          display: 'flex', alignItems: 'center', padding: '0 14px',
          transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        }}>
          <span style={{ lineHeight: 0, marginRight: 10, color: COLORS.mid }}>{Icons.search(COLORS.mid)}</span>
          <input
            ref={searchInputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Search documents…"
            style={{
              flex: 1, padding: '12px 0', border: 'none', outline: 'none',
              fontSize: 14, background: 'transparent', fontFamily: 'inherit', color: COLORS.dark,
            }}
          />
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            padding: '2px 6px', borderRadius: 5, background: COLORS.light,
            color: COLORS.mid, fontSize: 11, fontWeight: 600, letterSpacing: 0.4,
          }}>
            <span>⌘</span><span>K</span>
          </span>
        </div>

        {/* Filter button + panel */}
        <div ref={filterPanelRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setShowFilterPanel((s) => !s)}
            style={{
              padding: '10px 16px', borderRadius: 10,
              border: `1px solid ${hasActiveFilter ? COLORS.blue : COLORS.border}`,
              background: hasActiveFilter ? COLORS.blueSoft : COLORS.white,
              color: hasActiveFilter ? COLORS.blue : COLORS.dark,
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 8,
              boxShadow: SHADOWS.sm,
              transition: 'all 0.12s',
            }}
          >
            {Icons.filter(hasActiveFilter ? COLORS.blue : COLORS.mid)}
            Filter &amp; Sort
            {hasActiveFilter && (
              <span style={{
                background: COLORS.blue, color: '#fff', borderRadius: 999,
                padding: '0 6px', fontSize: 10, minWidth: 16, textAlign: 'center',
              }}>
                {[fileTypeFilter, folderFilter, sortKey !== 'modified_desc' ? 'sort' : ''].filter(Boolean).length}
              </span>
            )}
          </button>

          {showFilterPanel && (
            <div className="crm-anim-slideDown" style={{
              position: 'absolute', top: '100%', right: 0, marginTop: 8,
              background: COLORS.white, borderRadius: 12,
              border: `1px solid ${COLORS.border}`, padding: 16,
              boxShadow: SHADOWS.lg, zIndex: 100, minWidth: 280,
            }}>
              {/* Sort */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: COLORS.mid, fontWeight: 700, marginBottom: 8, letterSpacing: 0.5 }}>Sort by</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {SORT_OPTIONS.map((opt) => (
                    <button key={opt.value} onClick={() => setSortKey(opt.value)} style={optionBtnStyle(sortKey === opt.value)}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ borderTop: `1px solid ${COLORS.border}`, marginBottom: 14 }} />

              {/* File type */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: COLORS.mid, fontWeight: 700, marginBottom: 8, letterSpacing: 0.5 }}>File type</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {availableTypes.map((ext) => (
                    <button key={ext || '__all__'} onClick={() => setFileTypeFilter(ext)} style={optionBtnStyle(fileTypeFilter === ext)}>
                      {ext || 'All types'}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ borderTop: `1px solid ${COLORS.border}`, marginBottom: 14 }} />

              {/* Folder */}
              <div>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: COLORS.mid, fontWeight: 700, marginBottom: 8, letterSpacing: 0.5 }}>Folder</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {availableFolders.map((folder) => (
                    <button key={folder || '__all__'} onClick={() => setFolderFilter(folder)} style={optionBtnStyle(folderFilter === folder)}>
                      {folder || 'All folders'}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={() => { setSortKey('modified_desc'); setFileTypeFilter(''); setFolderFilter(''); }}
                style={{ fontSize: 12, color: COLORS.mid, cursor: 'pointer', border: 'none', background: 'none', padding: '4px 0', marginTop: 12, fontFamily: 'inherit' }}
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Active filter badges */}
      {hasActiveFilter && (
        <div style={{ marginBottom: 14, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          {fileTypeFilter && (
            <span style={{ background: COLORS.blueSoft, color: COLORS.blue, borderRadius: 999, padding: '4px 12px', fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              Type: {fileTypeFilter}
              <button onClick={() => setFileTypeFilter('')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: COLORS.blue, padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
            </span>
          )}
          {folderFilter && (
            <span style={{ background: COLORS.blueSoft, color: COLORS.blue, borderRadius: 999, padding: '4px 12px', fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              Folder: {folderFilter}
              <button onClick={() => setFolderFilter('')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: COLORS.blue, padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
            </span>
          )}
          {sortKey !== 'modified_desc' && (
            <span style={{ background: COLORS.blueSoft, color: COLORS.blue, borderRadius: 999, padding: '4px 12px', fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              Sort: {sortLabel}
              <button onClick={() => setSortKey('modified_desc')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: COLORS.blue, padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
            </span>
          )}
        </div>
      )}

      {loading && <SkeletonTable rows={6} widths={[42, 10, 18, 22]} />}
      {error && <ErrorBlock message={error} />}
      {!loading && !error && filteredAndSorted.length === 0 && (
        <EmptyState
          icon={Icons.inbox(COLORS.blue, 32)}
          title={isSearching ? `No results for “${debounced}”` : 'No documents indexed yet'}
          description={isSearching
            ? 'Try a different keyword, or clear filters to broaden the search.'
            : 'Once documents are uploaded to SharePoint and indexed, they will appear here.'}
        />
      )}

      {!loading && !error && filteredAndSorted.length > 0 && (
        <>
          <div style={{ fontSize: 12, color: COLORS.mid, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontWeight: 600, color: COLORS.dark }}>{filteredAndSorted.length}</span>
            <span>of {(data || []).length} documents</span>
            {isSearching && <span style={{ color: COLORS.mid }}>· matching “{debounced}”</span>}
          </div>

          {viewMode === 'list' ? (
            <div style={{ background: COLORS.white, borderRadius: 12, border: `1px solid ${COLORS.border}`, overflow: 'hidden', boxShadow: SHADOWS.sm }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <TH>Document</TH>
                    <TH>Type</TH>
                    <TH>Folder</TH>
                    <TH>Modified</TH>
                  </tr>
                </thead>
                <tbody className="crm-stagger">
                  {filteredAndSorted.map((d) => {
                    const ext = fileExt(d.name);
                    const folder = d.parentPath.split('/').filter(Boolean).slice(-1)[0] || '';
                    return (
                      <tr
                        key={d.id}
                        className="crm-row-hover"
                        onMouseEnter={(e) => (e.currentTarget.style.background = COLORS.pageBg)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                      >
                        <TD>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <IconTile ext={ext} size={36} />
                            {d.webUrl ? (
                              <a href={d.webUrl} target="_blank" rel="noreferrer" style={{ color: COLORS.dark, textDecoration: 'none', fontWeight: 500 }}>
                                {d.name}
                              </a>
                            ) : <span style={{ fontWeight: 500 }}>{d.name}</span>}
                          </span>
                        </TD>
                        <TD style={{ textTransform: 'uppercase', fontSize: 11, color: COLORS.mid, fontWeight: 600 }}>{ext}</TD>
                        <TD style={{ color: COLORS.mid, fontSize: 12 }}>
                          {folder ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                              {Icons.folder(COLORS.mid, 13)} {folder}
                            </span>
                          ) : ''}
                        </TD>
                        <TD style={{ color: COLORS.mid, fontSize: 12 }}>{formatDate(d.lastModified)}</TD>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="crm-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
              {filteredAndSorted.map((d) => {
                const ext = fileExt(d.name);
                const folder = d.parentPath.split('/').filter(Boolean).slice(-1)[0] || '';
                return (
                  <a
                    key={d.id}
                    href={d.webUrl || '#'}
                    target={d.webUrl ? '_blank' : undefined}
                    rel={d.webUrl ? 'noreferrer' : undefined}
                    className="crm-card-lift"
                    style={{
                      background: COLORS.white,
                      borderRadius: 14, border: `1px solid ${COLORS.border}`,
                      padding: 16, textDecoration: 'none', color: 'inherit',
                      display: 'flex', flexDirection: 'column', gap: 10,
                      boxShadow: SHADOWS.sm, minHeight: 140,
                    }}
                  >
                    <IconTile ext={ext} size={48} />
                    <div style={{
                      fontSize: 13, fontWeight: 600, color: COLORS.dark, lineHeight: 1.35,
                      overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      wordBreak: 'break-word',
                    }}>{d.name}</div>
                    <div style={{ marginTop: 'auto', fontSize: 11, color: COLORS.mid, display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {folder && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          {Icons.folder(COLORS.mid, 12)} {folder}
                        </span>
                      )}
                      <span>{formatDate(d.lastModified)}</span>
                    </div>
                  </a>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DocumentsView;
